import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

/**
 * Slide-in detail panel for activity_log and security events.
 * Used by GlobalActivityLog and GlobalSecurityEvents.
 */
function EventDetailPanel({ event, onClose }) {
  const [panelTop, setPanelTop] = useState(65)

  useEffect(() => {
    if (!event) return
    const measure = () => {
      const header = document.querySelector('[data-main-header]')
      if (header) setPanelTop(header.getBoundingClientRect().bottom)
    }
    measure()
    const timer = setTimeout(measure, 50)
    return () => clearTimeout(timer)
  }, [event])

  if (!event) return null

  const actionLabel = (event.action || '').replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  const getActionColor = (action) => {
    if (action?.includes('failed')) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    if (action?.includes('login') || action?.includes('mfa')) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    if (action?.includes('created') || action?.includes('invited')) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    if (action?.includes('deleted')) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    if (action?.includes('updated')) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
    return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
  }

  const formatVal = (v) => {
    if (v === null || v === undefined || v === '') return 'Empty'
    if (Array.isArray(v)) return v.join(', ')
    if (typeof v === 'object') return JSON.stringify(v)
    if (typeof v === 'boolean') return v ? 'Yes' : 'No'
    return String(v)
  }

  const metadata = event.metadata || {}
  const changes = metadata.changes || null
  const metaEntries = Object.entries(metadata).filter(([k]) => !['changes', 'fields', 'userAgent'].includes(k))

  return createPortal(
    <div className="fixed right-0 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl overflow-y-auto" style={{ top: `${panelTop}px`, height: `calc(100vh - ${panelTop}px)`, zIndex: 9999 }}>
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between z-10">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Event Details</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

      <div className="px-5 py-4 space-y-5">
        {/* Action badge */}
        <div>
          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getActionColor(event.action)}`}>
            {event.action}
          </span>
        </div>

        {/* Timestamp */}
        <div>
          <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Timestamp</p>
          <p className="text-sm text-gray-900 dark:text-white">
            {new Date(event.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(event.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
          </p>
        </div>

        {/* Organization */}
        {event.org_name && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Organization</p>
            <p className="text-sm text-gray-900 dark:text-white">{event.org_name}</p>
          </div>
        )}

        {/* User */}
        {event.user_email && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">User</p>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-xs font-medium text-primary-700 dark:text-primary-300">
                {event.user_email[0].toUpperCase()}
              </div>
              <p className="text-sm text-gray-900 dark:text-white">{event.user_email}</p>
            </div>
          </div>
        )}

        {/* Resource */}
        {(event.resource_type || event.resource_id) && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Resource</p>
            {event.resource_type && (
              <p className="text-sm text-gray-900 dark:text-white capitalize">{event.resource_type.replace(/_/g, ' ')}</p>
            )}
            {event.resource_id && (
              <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-0.5">{event.resource_id}</p>
            )}
          </div>
        )}

        {/* Event ID */}
        <div>
          <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Event ID</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">{event.id}</p>
        </div>

        {/* What Changed (if changes object exists) */}
        {changes && Object.keys(changes).length > 0 && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">What Changed</p>
            <div className="space-y-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {Object.entries(changes).map(([field, change]) => {
                const label = field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
                return (
                  <div key={field}>
                    <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
                    <div className="flex items-start gap-2 text-[12px]">
                      <span className="text-red-500 dark:text-red-400 line-through break-words flex-1">{formatVal(change.from)}</span>
                      <span className="text-gray-300 dark:text-gray-600 flex-shrink-0 mt-0.5">→</span>
                      <span className="text-green-600 dark:text-green-400 font-medium break-words flex-1">{formatVal(change.to)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Session Details (login events) */}
        {(event.action?.includes('login') || event.action?.includes('mfa')) && metadata.browser && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Session Details</p>
            <div className="space-y-1.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {[
                { label: 'Browser', value: metadata.browser },
                { label: 'OS', value: metadata.os },
                { label: 'Platform', value: metadata.platform },
                { label: 'Language', value: metadata.language },
                { label: 'Timezone', value: metadata.timezone },
                { label: 'Screen', value: metadata.screen ? `${metadata.screen.width} x ${metadata.screen.height}` : null },
                { label: 'Viewport', value: metadata.viewport ? `${metadata.viewport.width} x ${metadata.viewport.height}` : null },
                { label: 'Network', value: metadata.network?.effectiveType },
                { label: 'Downlink', value: metadata.network?.downlink != null ? `${metadata.network.downlink} Mbps` : null },
                { label: 'Latency', value: metadata.network?.rtt != null ? `${metadata.network.rtt} ms` : null }
              ].filter(d => d.value).map(d => (
                <div key={d.label} className="flex items-baseline justify-between">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">{d.label}</span>
                  <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information */}
        {metaEntries.length > 0 && !metadata.browser && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Additional Information</p>
            <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {metaEntries.map(([key, value]) => {
                const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                return (
                  <div key={key}>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
                    <p className="text-[12px] text-gray-800 dark:text-gray-200 break-words">{formatVal(value)}</p>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* User Agent */}
        {metadata.userAgent && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">User Agent</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono break-all leading-relaxed bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              {metadata.userAgent}
            </p>
          </div>
        )}

        {/* Raw Data */}
        {Object.keys(metadata).length > 0 && (
          <div>
            <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Raw Data</p>
            <pre className="text-[11px] text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words leading-relaxed">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default EventDetailPanel
